import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { DownloadIcon, CalendarIcon } from 'lucide-react';
import { format, subDays, addDays, isSameDay } from 'date-fns';

interface DownloadUserDetailsProps {
  onDownload?: (startDate: Date, endDate: Date) => void;
  className?: string;
}

export const DownloadUserDetails: React.FC<DownloadUserDetailsProps> = ({
  onDownload,
  className = '',
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectionType, setSelectionType] = useState<'single' | 'range'>(
    'range'
  );
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    const days = [];
    for (let i = 0; i < 42; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }

    return days;
  };

  const handleDateClick = (date: Date) => {
    if (selectionType === 'single') {
      setRangeStart(date);
      setRangeEnd(date);
    } else {
      if (!rangeStart || (rangeStart && rangeEnd)) {
        setRangeStart(date);
        setRangeEnd(null);
      } else if (rangeStart && !rangeEnd) {
        if (date < rangeStart) {
          setRangeEnd(rangeStart);
          setRangeStart(date);
        } else {
          setRangeEnd(date);
        }
      }
    }
  };

  const handlePrevMonth = () => {
    setSelectedDate((prev) => subDays(prev, 30));
  };

  const handleNextMonth = () => {
    setSelectedDate((prev) => addDays(prev, 30));
  };

  const handleQuickSelect = (days: number) => {
    const end = new Date();
    const start = subDays(end, days - 1);
    setRangeStart(start);
    setRangeEnd(end);
    setSelectedDate(end);
  };

  const handleDownload = () => {
    if (!rangeStart || !rangeEnd) {
      toast.error('Please select a date range');
      return;
    }

    if (onDownload) {
      onDownload(rangeStart, rangeEnd);
    } else {
      // Default download logic would go here
    }
  };

  const days = generateCalendarDays();
  const monthYear = format(selectedDate, 'MMMM yyyy');

  const isDateInRange = (date: Date) => {
    if (!rangeStart || !rangeEnd) return false;
    return date >= rangeStart && date <= rangeEnd;
  };

  const isDateSelected = (date: Date) => {
    if (rangeStart && isSameDay(date, rangeStart)) return true;
    if (rangeEnd && isSameDay(date, rangeEnd)) return true;
    return false;
  };

  return (
    <div className={`bg-card  rounded-xl p-6 ${className}`}>
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Calendar Section */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-text flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              Select Date Range
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectionType('single')}
                className={`px-3 py-1 text-sm rounded ${selectionType === 'single' ? 'bg-teal text-white' : 'bg-gray-100 text-gray-700'}`}
              >
                Single Day
              </button>
              <button
                onClick={() => setSelectionType('range')}
                className={`px-3 py-1 text-sm rounded ${selectionType === 'range' ? 'bg-teal text-white' : 'bg-gray-100 text-gray-700'}`}
              >
                Date Range
              </button>
            </div>
          </div>

          {/* Quick Select Buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => handleQuickSelect(7)}
              className="px-3 py-1.5 text-sm bg-foreground border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              Last 7 Days
            </button>
            <button
              onClick={() => handleQuickSelect(30)}
              className="px-3 py-1.5 text-sm bg-foreground border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              Last 30 Days
            </button>
            <button
              onClick={() => handleQuickSelect(90)}
              className="px-3 py-1.5 text-sm bg-foreground border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              Last 90 Days
            </button>
          </div>

          {/* Selected Dates Display */}
          <div className="mb-4 p-3 bg-foreground border border-gray-200 rounded">
            <p className="text-sm text-gray-600">Selected Range:</p>
            <p className="font-medium text-text">
              {rangeStart ? format(rangeStart, 'MMM dd, yyyy') : 'Start date'}
              {' → '}
              {rangeEnd ? format(rangeEnd, 'MMM dd, yyyy') : 'End date'}
            </p>
          </div>

          {/* Calendar */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Calendar Header */}
            <div className=" flex items-center justify-between bg-teal text-white p-3">
              <button
                onClick={handlePrevMonth}
                className="text-4xl p-1 hover:bg-teal/80 rounded transition-colors"
              >
                ‹
              </button>
              <h4 className="text-xl font-bold">{monthYear}</h4>
              <button
                onClick={handleNextMonth}
                className="text-4xl p-1 hover:bg-teal/80 rounded transition-colors"
              >
                ›
              </button>
            </div>

            {/* Days of Week */}
            <div className="grid grid-cols-7 bg-gray-50 border-b">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div
                  key={day}
                  className="p-2 text-center text-sm font-medium text-gray-600"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7">
              {days.map((day, index) => {
                const isCurrentMonth =
                  day.getMonth() === selectedDate.getMonth();
                const isToday = isSameDay(day, new Date());
                const inRange = isDateInRange(day);
                const selected = isDateSelected(day);
                const isStart = rangeStart && isSameDay(day, rangeStart);
                const isEnd = rangeEnd && isSameDay(day, rangeEnd);

                return (
                  <button
                    key={index}
                    onClick={() => handleDateClick(day)}
                    className={`
                      h-12 relative border border-gray-100
                      ${!isCurrentMonth ? 'text-gray-400 bg-gray-50' : 'text-text hover:bg-gray-50'}
                      ${inRange ? 'bg-teal/10' : ''}
                      ${selected ? 'font-bold' : ''}
                      ${isToday ? 'ring-2 ring-teal/30' : ''}
                      ${isStart || isEnd ? 'bg-teal text-white hover:bg-teal' : ''}
                      transition-colors
                    `}
                  >
                    <span className="absolute inset-0 flex items-center justify-center">
                      {day.getDate()}
                    </span>
                    {(isStart || isEnd) && (
                      <span className="absolute top-1 right-1 text-xs">
                        {isStart ? 'S' : 'E'}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Download Section */}
        <div className="lg:w-64 flex flex-col justify-between">
          <div>
            <h3 className="text-xl font-bold text-text mb-4">Export Data</h3>
            <p className="text-gray-600 mb-4">
              Select a date range above to export user details.
            </p>
          </div>

          <button
            onClick={handleDownload}
            disabled={!rangeStart || !rangeEnd}
            className={`
              mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium
              ${
                rangeStart && rangeEnd
                  ? 'bg-teal text-white hover:bg-teal/90'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }
              transition-colors
            `}
          >
            <DownloadIcon className="h-5 w-5" />
            Download Report
            {rangeStart && rangeEnd && (
              <span className="text-sm opacity-90">
                ({format(rangeStart, 'MMM dd')} - {format(rangeEnd, 'MMM dd')})
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
